import type { AxiosResponse } from 'axios';
import { AxiosError } from 'axios';

import api from '@/utils/axiosInterceptor';

interface VerifyAuthResponse {
  authenticated: boolean;
  user: any | null;
  message?: string;
}

interface CheckValidApiResponse {
  success: boolean;
  data?: any;
  message?: string;
  authError?: boolean;
  connectionError?: boolean;
  statusCode?: number;
}

export async function CheckValidApi(): Promise<CheckValidApiResponse> {
  try {
    const response: AxiosResponse<VerifyAuthResponse> =
      await api.get(`/auth/verify`);

    // Transform the /auth/verify response to match expected format
    return {
      success: response.data.authenticated,
      data: response.data.user,
      message:
        response.data.message ||
        (response.data.authenticated ? 'Authenticated' : 'Not authenticated'),
    };
  } catch (err) {

    if (err instanceof AxiosError) {
      // Check if this is a 401 error
      if (err.response?.status === 401) {
        // The interceptor has already handled token refresh attempts
        // If we still get 401, the refresh failed and user is not authenticated
        return {
          success: false,
          message: 'Authentication failed - please login again',
          data: null,
          authError: true, // Flag to indicate this is an auth error
        };
      }

      // Handle network/connection errors
      if (!err.response) {
        console.error(
          '🌐 [CheckValidApi] Network error during authentication check:',
          err.message,
        );
        // For network errors, we don't want to logout the user
        // Let React Query retry handle this
        throw new Error(`Network error: ${err.message}`);
      }

      // Handle other HTTP errors (403, 5xx, etc.)
      if (err.response.status >= 500) {
        console.error('🔴 [CheckValidApi] Server error:', err.response.status);
        // Server errors should be retried
        throw new Error(`Server error: ${err.response.status}`);
      }

      // Handle other client errors (403, etc.)
      const errorMessage =
        err.response?.data?.message || err.message || 'Unknown error occurred';
      console.error('🔴 [CheckValidApi] Client error:', errorMessage);

      return {
        success: false,
        message: errorMessage,
        data: null,
        statusCode: err.response.status,
      };
    } else {
      console.error('🔴 [CheckValidApi] Unexpected error:', err);
      // Unexpected errors should be retried
      throw new Error('An unexpected error occurred');
    }
  }
}

// Version Check API Types
interface VersionChangelogItem {
  type: 'fix' | 'new feature';
  title: string;
  media?: string[];
}

interface LatestVersionData {
  _id: string;
  version: string;
  buildId: string;
  isForced: boolean;
  hasSeenVersion: boolean; // Make this required as backend always sends it
  changelog: VersionChangelogItem[];
  publishedAt: string;
}

interface LatestVersionResponse {
  success: boolean;
  data: LatestVersionData | null;
  message?: string;
}

interface MarkVersionSeenRequest {
  userId: string;
  version: string;
}

interface MarkVersionSeenResponse {
  success: boolean;
  data: {
    userId: string;
    version: string;
    seenAt: string;
  };
  message?: string;
}

/**
 * Get the latest app version from the server
 */
export async function getLatestVersion(): Promise<LatestVersionResponse> {
  try {
    const response: AxiosResponse<LatestVersionResponse> = await api.get(
      '/app/version/latest',
    );

    return response.data;
  } catch (err) {

    if (err instanceof AxiosError) {
      // Handle network/connection errors
      if (!err.response) {
        console.error('🌐 [getLatestVersion] Network error:', err.message);
        throw new Error(`Network error: ${err.message}`);
      }

      // Handle 404 - no versions found
      if (err.response.status === 404) {
        return {
          success: true,
          data: null,
          message: 'No versions published yet',
        };
      }

      // Handle other HTTP errors
      const errorMessage =
        err.response?.data?.message || err.message || 'Unknown error occurred';
      console.error('🔴 [getLatestVersion] API error:', errorMessage);

      return {
        success: false,
        data: null,
        message: errorMessage,
      };
    } else {
      console.error('🔴 [getLatestVersion] Unexpected error:', err);
      throw new Error('An unexpected error occurred');
    }
  }
}

/**
 * Mark a version as seen by the user
 */
export async function markVersionSeen(
  data: MarkVersionSeenRequest,
): Promise<MarkVersionSeenResponse> {
  try {
    const response: AxiosResponse<MarkVersionSeenResponse> = await api.post(
      '/app/version/seen',
      data,
    );

    return response.data;
  } catch (err) {

    if (err instanceof AxiosError) {
      // Handle network/connection errors
      if (!err.response) {
        console.error('🌐 [markVersionSeen] Network error:', err.message);
        throw new Error(`Network error: ${err.message}`);
      }

      // Handle auth errors
      if (err.response.status === 401) {
        console.error('🔴 [markVersionSeen] Authentication required');
        return {
          success: false,
          data: {
            userId: data.userId,
            version: data.version,
            seenAt: new Date().toISOString(),
          },
          message: 'Authentication required',
        };
      }

      // Handle other HTTP errors
      const errorMessage =
        err.response?.data?.message || err.message || 'Unknown error occurred';
      console.error('🔴 [markVersionSeen] API error:', errorMessage);

      return {
        success: false,
        data: {
          userId: data.userId,
          version: data.version,
          seenAt: new Date().toISOString(),
        },
        message: errorMessage,
      };
    } else {
      console.error('🔴 [markVersionSeen] Unexpected error:', err);
      throw new Error('An unexpected error occurred');
    }
  }
}

// Logout API Types
interface LogoutRequest {
  isReset?: boolean;
}

interface LogoutResponse {
  success: boolean;
  message?: string;
  isReset?: boolean;
}

/**
 * Unified logout function with proper error handling
 * This replaces the problematic logoutApi function
 */
export async function logoutUser(isReset: boolean = false): Promise<{
  data: LogoutResponse;
  isReset: boolean;
}> {

  try {
    const response: AxiosResponse<LogoutResponse> = await api.post(
      '/auth/logout',
      { isReset } as LogoutRequest,
    );


    // Clear local storage regardless of server response
    try {
      localStorage.removeItem('user_id');
      sessionStorage.removeItem('user_id');
    } catch (storageError) {
      console.warn('⚠️ [logoutUser] Storage cleanup warning:', storageError);
    }

    return {
      data: {
        success: true,
        message: response.data.message || 'Logout successful',
        isReset,
      },
      isReset,
    };
  } catch (err) {

    // Always clear local data even if server logout fails
    try {
      localStorage.removeItem('user_id');
      sessionStorage.removeItem('user_id');
    } catch (storageError) {
      console.warn('⚠️ [logoutUser] Storage cleanup warning:', storageError);
    }

    if (err instanceof AxiosError) {
      // Handle 401 - this is expected when token is already expired
      if (err.response?.status === 401) {
        // This is actually successful - user is already logged out
        return {
          data: {
            success: true,
            message: 'Logout completed',
            isReset,
          },
          isReset,
        };
      }

      // Handle network/connection errors
      if (!err.response) {
        console.error(
          '🌐 [logoutUser] Network error during logout:',
          err.message,
        );
        // Don't throw for network errors - still allow logout to proceed
        return {
          data: {
            success: true,
            message: 'Logout completed (offline)',
            isReset,
          },
          isReset,
        };
      }

      // Handle server errors
      if (err.response.status >= 500) {
        console.error(
          '🔴 [logoutUser] Server error during logout:',
          err.response.status,
        );
        // Don't throw for server errors - still allow logout to proceed
        return {
          data: {
            success: true,
            message: 'Logout completed (server error)',
            isReset,
          },
          isReset,
        };
      }

      // Handle other HTTP errors (401, 403, etc.)
      const errorMessage =
        err.response?.data?.message || err.message || 'Logout failed';
      console.error('🔴 [logoutUser] HTTP error during logout:', errorMessage);

      // For client errors, still proceed with logout but log the error
      return {
        data: {
          success: true,
          message: 'Logout completed (with warnings)',
          isReset,
        },
        isReset,
      };
    } else {
      console.error('🔴 [logoutUser] Unexpected error during logout:', err);

      // Even for unexpected errors, proceed with logout
      return {
        data: {
          success: true,
          message: 'Logout completed (unexpected error)',
          isReset,
        },
        isReset,
      };
    }
  }
}
